import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const BlogTermsOfUse = () => {
  return (
    <div className="min-h-screen pt-20 bg-[#F8F9FA]">
      <SEO 
        title="Blog Terms of Use"
        description="Terms of Use for Navaris Digital blog and content sections."
        canonicalUrl="/blog/terms-of-use"
      />
      
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-[#320547] via-[#4a1a6b] to-[#95228A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-6">
              <FileText size={32} className="text-white" />
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white mb-4">
              Blog Terms of Use
            </h1>
            <p className="text-white/70 text-lg">
              Terms and conditions for using our blog content
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Button
            asChild
            variant="ghost"
            className="mb-8 text-gray-500 hover:text-[#320547] -ml-4"
          >
            <Link to="/blog">
              <ArrowLeft className="mr-2" size={18} /> Back to Blog
            </Link>
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-gray-100">
              <p className="text-gray-600 leading-relaxed text-lg mb-8">
                Welcome to the Navaris Digital blog. By accessing, browsing, or using the blog and content sections of{' '}
                <a href="https://www.navarisdigital.com" className="text-[#95228A] hover:text-[#320547]">
                  https://www.navarisdigital.com
                </a>, you agree to comply with and be bound by the following Terms of Use. Please read them carefully before using our blog.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                By accessing our blog, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our blog.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                2. Intellectual Property Rights
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                All content published on the Navaris Digital blog, including but not limited to articles, text, graphics, images, logos, and software, is the property of Navaris Digital or its content creators and is protected by applicable copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, create derivative works from, publicly display, or otherwise use any content from our blog without prior written permission from Navaris Digital.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                3. Use of Content
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Blog content is provided for informational and educational purposes only. The views and opinions expressed in blog posts are those of the authors and do not necessarily reflect the official policy or position of Navaris Digital. Content should not be considered as professional advice, and readers are encouraged to seek qualified professional guidance for specific situations.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                4. User Conduct
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                When interacting with our blog through comments, subscriptions, or other features, you agree to: provide accurate and truthful information; refrain from posting content that is unlawful, harmful, threatening, abusive, defamatory, or otherwise objectionable; respect the intellectual property rights of others; not attempt to gain unauthorized access to any portion of the blog or its related systems.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                5. Comments and User Submissions
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Navaris Digital reserves the right to review, edit, or remove any user-submitted content, including comments, at our sole discretion. By submitting content, you grant Navaris Digital a non-exclusive, royalty-free, perpetual license to use, reproduce, and display such content in connection with the blog.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                6. Third-Party Links
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our blog may contain links to third-party websites or resources. These links are provided for convenience only, and Navaris Digital does not endorse or assume responsibility for the content, products, or services offered by third parties.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                7. Disclaimer of Warranties
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Blog content is provided "as is" without warranties of any kind, either express or implied. Navaris Digital makes no representations or warranties regarding the accuracy, completeness, reliability, or suitability of any content for a particular purpose.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                8. Limitation of Liability
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                To the fullest extent permitted by law, Navaris Digital shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of or inability to use the blog or its content.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                9. Modifications to Terms
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Navaris Digital reserves the right to modify these Terms of Use at any time. Changes will be effective immediately upon posting on this page. Your continued use of the blog after any modifications constitutes acceptance of the updated terms.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                10. Governing Law
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                These Terms of Use shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                Contact Us
              </h2>
              <p className="text-gray-600 leading-relaxed">
                If you have any questions about these Terms of Use, please contact us at{' '}
                <a 
                  href="mailto:info@navarisdigital.com" 
                  className="text-[#95228A] hover:text-[#320547] inline-flex items-center gap-1"
                >
                  <Mail size={16} />
                  info@navarisdigital.com
                </a>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogTermsOfUse;
